import {
  FEATURE_FOR_PRODUCT_VALUES,
  type FeatureForProduct,
  type IFeatureEvaluationContext,
  type IFeaturePsetProps,
} from './feature-helper.types';

function parseFeatureFor(raw: unknown): FeatureForProduct {
  if (
    typeof raw === 'string' &&
    (FEATURE_FOR_PRODUCT_VALUES as readonly string[]).includes(raw)
  ) {
    return raw as FeatureForProduct;
  }
  return 'all';
}

function normalizeStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const out = value.filter((item): item is string => typeof item === 'string');
  return out.length > 0 ? out : undefined;
}

/**
 * Reads feature payload fields from a CDM / pset-api response object.
 * Missing `isOn` defaults to true; missing `isScoped` defaults to false.
 */
export function parseFeaturePsetPayload(
  response: unknown
): IFeaturePsetProps | null {
  if (response === null || response === undefined) {
    return null;
  }
  if (typeof response !== 'object') {
    return null;
  }
  const obj = response as Record<string, unknown>;
  const rawProps = obj['props'];
  const props =
    rawProps !== undefined && typeof rawProps === 'object' && rawProps !== null
      ? (rawProps as Record<string, unknown>)
      : obj;

  const isOn = props['isOn'];
  const isScoped = props['isScoped'];

  return {
    featureFor: parseFeatureFor(props['featureFor']),
    isOn: isOn === false ? false : true,
    isScoped: isScoped === true,
    accountIds: normalizeStringArray(props['accountIds']),
    projectIds: normalizeStringArray(props['projectIds']),
    groupIds: normalizeStringArray(props['groupIds']),
    userIds: normalizeStringArray(props['userIds']),
  };
}

/**
 * When `isScoped` is true, the feature is only active if the current context
 * matches every non-empty **account / project / group** list on the pset (AND).
 * Empty / missing lists do not impose that dimension.
 *
 * `userIds` is **not** evaluated here: pset policy (e.g. `claims.sub` ∈ `userIds`)
 * enforces access at read time. If the client received the pset, user allowlisting
 * is already satisfied.
 *
 * If `isScoped` is true but there are no account/project/group lists and no
 * `userIds` list, returns false (misconfigured). If only `userIds` is set, returns
 * true (access control only; no extra UI boundary).
 */
export function evaluateScopedContext(
  props: IFeaturePsetProps,
  context: IFeatureEvaluationContext
): boolean {
  const { accountIds, projectIds, groupIds, userIds } = props;

  const hasAccount = accountIds !== undefined && accountIds.length > 0;
  const hasProject = projectIds !== undefined && projectIds.length > 0;
  const hasGroup = groupIds !== undefined && groupIds.length > 0;
  const hasUserList = userIds !== undefined && userIds.length > 0;

  if (!hasAccount && !hasProject && !hasGroup) {
    if (hasUserList) {
      return true;
    }
    return false;
  }

  if (hasAccount) {
    if (
      context.accountId === undefined ||
      context.accountId === '' ||
      !accountIds!.includes(context.accountId)
    ) {
      return false;
    }
  }

  if (hasProject) {
    if (
      context.projectId === undefined ||
      context.projectId === '' ||
      !projectIds!.includes(context.projectId)
    ) {
      return false;
    }
  }

  if (hasGroup) {
    const userGroups = context.groupIds ?? [];
    if (!groupIds!.some((g) => userGroups.includes(g))) {
      return false;
    }
  }

  return true;
}

/**
 * Full client-side evaluation: document present, `isOn`, then optional scoping.
 */
export function evaluateFeatureFromPsetProps(
  props: IFeaturePsetProps | null | undefined,
  context?: IFeatureEvaluationContext
): boolean {
  if (props === null || props === undefined) {
    return false;
  }
  if (props.isOn === false) {
    return false;
  }
  if (!props.isScoped) {
    return true;
  }
  if (context === undefined) {
    return false;
  }
  return evaluateScopedContext(props, context);
}
